/**
 * ProjectOverviewCard Component
 * Unified collapsible card containing all sales form information:
 * - Client details (name, phone, email, IC)
 * - Property details (address, type, size)
 * - Project scope and special requirements
 * - Preferred start date and current deadline
 */

import React, { useState } from 'react';
import {
  formatPhoneNumber,
  formatPropertyType,
  formatDate
} from '../../utils/formatters';

const ProjectOverviewCard = ({ project, isSiteDraughter = false, isRestrictedViewer = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const salesForm = project?.salesForm || {};

  // Check if viewer has restricted access (site draughter or 2D draughter)
  const hasRestrictedAccess = isSiteDraughter || isRestrictedViewer;

  // Build full address
  const getFullAddress = () => {
    if (salesForm.addressLine1) {
      const parts = [
        salesForm.addressLine1,
        salesForm.addressLine2,
        salesForm.postcode,
        salesForm.city,
        salesForm.state
      ].filter(Boolean);
      return parts.join(', ');
    }
    return salesForm.propertyAddress || 'N/A';
  };

  // Restricted viewers (site draughters, 2D draughters) have limited view - no client contact info
  if (hasRestrictedAccess) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Property Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <div className="md:col-span-2">
            <p className="text-xs sm:text-sm text-gray-500">Address</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{getFullAddress()}</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Property Type</p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {formatPropertyType(salesForm.propertyType)}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Property Size</p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {salesForm.propertySize ? `${Number(salesForm.propertySize).toLocaleString()} sq ft` : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header - touch friendly */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Project Overview</h2>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium focus:outline-none min-h-[44px] px-2 -mr-2 touch-manipulation flex-shrink-0"
        >
          <span className="hidden xs:inline">{isExpanded ? 'Show Less' : 'Show More'}</span>
          <span className="xs:hidden">{isExpanded ? 'Less' : 'More'}</span>
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Preview Content - Always visible */}
      <div className="px-4 sm:px-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Client Info Row */}
          <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm font-medium text-gray-900">{salesForm.clientName || 'N/A'}</span>
            </div>
            {salesForm.clientPhone && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href={`tel:${salesForm.clientPhone}`} className="text-sm text-blue-600 hover:text-blue-700">
                  {formatPhoneNumber(salesForm.clientPhone)}
                </a>
              </div>
            )}
            {salesForm.clientEmail && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href={`mailto:${salesForm.clientEmail}`} className="text-sm text-blue-600 hover:text-blue-700">
                  {salesForm.clientEmail}
                </a>
              </div>
            )}
          </div>

          {/* Property Info Row */}
          <div className="md:col-span-2">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-900">{getFullAddress()}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatPropertyType(salesForm.propertyType)}
                  {salesForm.propertySize && ` • ${Number(salesForm.propertySize).toLocaleString()} sq ft`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Extended Content - Collapsible */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-2 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {/* IC Number */}
            <div>
              <p className="text-sm text-gray-500">IC Number</p>
              <p className="mt-1 text-sm font-medium text-gray-900">{salesForm.clientIC || 'N/A'}</p>
            </div>

            {/* Preferred Start Date */}
            <div>
              <p className="text-sm text-gray-500">Preferred Start Date</p>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {salesForm.preferredStartDate ? formatDate(salesForm.preferredStartDate) : 'N/A'}
              </p>
            </div>

            {/* Current Deadline */}
            <div>
              <p className="text-sm text-gray-500">Current Deadline</p>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {project.currentDeadline ? formatDate(project.currentDeadline) : 'No deadline set'}
              </p>
            </div>

            {/* Project Scope */}
            {salesForm.projectScope && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Project Scope</p>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                  {salesForm.projectScope}
                </p>
              </div>
            )}

            {/* Special Requirements */}
            {salesForm.specialRequirements && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Special Requirements</p>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  {salesForm.specialRequirements}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverviewCard;
